import { create } from 'zustand';
import { applyUpdate as applyServiceWorkerUpdate } from '../serviceWorkerRegistration';

const VERSION_URL = '/version.json';
const PLACEHOLDER_VERSION = '{{VERSION}}';

export const isPlaceholderVersion = (version) => !version || version === PLACEHOLDER_VERSION;

// Single source of truth for "is an update available", shared by the
// Settings dialog, the background notifier, and the NavBar badge so they
// can never disagree with each other.
export const selectUpdateAvailable = (state) => {
  const localVersion = state.localVersionInfo?.version;
  return (
    !isPlaceholderVersion(localVersion) &&
    !isPlaceholderVersion(state.remoteVersion) &&
    localVersion !== state.remoteVersion
  );
};

const fetchVersionInfo = async () => {
  // cache: 'no-store' bypasses the browser's HTTP cache so this always talks
  // to the network when online. No query-string cache-buster: the service
  // worker's networkFirstStrategy caches GET responses keyed by the full
  // request URL, so a changing query string would pile up a new Cache
  // Storage entry on every check instead of keeping one stable
  // /version.json entry to fall back to when offline.
  const response = await fetch(VERSION_URL, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to fetch version.json: ${response.status}`);
  }
  return response.json();
};

export const useUpdateStore = create((set, get) => ({
  // The version this tab is currently running (loaded once and kept for the
  // session, so it reflects what's actually active rather than a moving target).
  localVersionInfo: null,
  // The version currently deployed on the server, refreshed on demand.
  remoteVersion: null,
  isChecking: false,
  isApplying: false,
  // The remote version we've already shown a toast for, so background
  // checks don't re-notify about the same available update over and over.
  notifiedVersion: null,

  loadLocalVersion: async () => {
    // Only skip if we already have a real, resolved version - not the null
    // fallback set below on failure, and not an unreplaced '{{VERSION}}'
    // placeholder - so a later retry (e.g. Settings reopened once
    // connectivity returns) can still succeed.
    if (!isPlaceholderVersion(get().localVersionInfo?.version)) return;
    try {
      const data = await fetchVersionInfo();
      set({ localVersionInfo: data });
    } catch (error) {
      console.error('Failed to load local version:', error);
      // Resolve to a known "unknown version" state instead of leaving
      // localVersionInfo null, which otherwise leaves Version.jsx stuck
      // rendering "Loading version..." forever.
      set({ localVersionInfo: { version: null, buildDate: null, commitSha: null } });
    }
  },

  checkForUpdate: async () => {
    set({ isChecking: true });
    try {
      const data = await fetchVersionInfo();
      set({ remoteVersion: data.version, isChecking: false });
    } catch (error) {
      console.error('Failed to check for update:', error);
      set({ isChecking: false });
    }
  },

  applyUpdate: () => {
    set({ isApplying: true });
    applyServiceWorkerUpdate();
  },

  markNotified: (version) => set({ notifiedVersion: version }),
}));
